import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Paper,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Dispatch, SetStateAction } from "react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useSortable } from "@dnd-kit/react/sortable";
import { CategoryItem } from "../../types";
import { renderCategoryIcon } from "../common/Category";
import { RootState } from "../../store/store";

const CategoryItemStyle = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 50,
  boxSizing: "border-box",
  gap: theme.spacing(1),
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(0, 1.5),
  borderRadius: "10px",
  color: (theme.vars ?? theme).palette.text.primary,
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.18)",
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));

interface CategoryItemComponentProps {
  type: "income" | "expense";
  item: CategoryItem;
  index: number;
  categoryLabel: string;
  setNewCategoryLabel: Dispatch<SetStateAction<string>>;
  onAddCategory: (type: "income" | "expense") => void;
  onCancelCategory: () => void;
  onDeleteCategoryItem: (type: string, item: CategoryItem) => void;
}

export const CategoryItemComponent = ({
  type,
  item,
  index,
  onDeleteCategoryItem,
}: CategoryItemComponentProps) => {
  const { category } = useSelector((state: RootState) => state.app);
  const { ref } = useSortable({ id: item.label, index });

  return (
    <div ref={ref}>
      <CategoryItemStyle elevation={0}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", color: "text.secondary" }}>
            {renderCategoryIcon(item.label, item.icon)}
          </Box>
          <Typography component="span" noWrap>
            {item.label}
          </Typography>
        </Box>
        <Box>
          {!item.isDefault && (
            <DeleteOutlinedIcon
              onClick={() => onDeleteCategoryItem(type, item)}
            />
          )}
          <DragIndicatorIcon
            ref={ref}
            aria-label={`${item.label}の並べ替え`}
            sx={{ color: "text.secondary", flexShrink: 0 }}
          />
        </Box>
      </CategoryItemStyle>
    </div>
  );
};

export const AddCategoryItemComponent = ({
  type,
  categoryLabel,
  setNewCategoryLabel,
  onAddCategory,
  onCancelCategory,
}: CategoryItemComponentProps) => {
  return (
    <CategoryItemStyle elevation={0}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        variant="standard"
        placeholder="カテゴリ名"
        value={categoryLabel}
        // inputProps={{ "aria-label": `${title}カテゴリ名` }}
        onChange={(event) => setNewCategoryLabel(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") onAddCategory(type);
          if (event.key === "Escape") onCancelCategory();
        }}
      />
      <IconButton
        aria-label="追加をキャンセル"
        size="small"
        onClick={onCancelCategory}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </CategoryItemStyle>
  );
};
